import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { CONFIG } from '../global-config';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.serverUrl });

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Ignorar errores de cancelación - no son errores reales
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Mantener la estructura completa del error para un mejor manejo
    if (error.response) {
      // El servidor respondió con un código de error
      return Promise.reject(error);
    } else if (error.request) {
      // La petición se hizo pero no se recibió respuesta
      return Promise.reject(new Error('No se pudo conectar con el servidor'));
    } else {
      // Algo pasó configurando la petición
      return Promise.reject(new Error(error.message || 'Error inesperado'));
    }
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------
// Request Cancellation Manager
// ----------------------------------------------------------------------

/**
 * Gestor de cancelación de peticiones HTTP.
 * Permite cancelar todas las peticiones pendientes de una página/contexto específico
 * cuando el usuario navega a otra vista.
 */
class RequestCancellationManager {
  private controllers: Map<string, AbortController[]> = new Map();

  /**
   * Crea un nuevo AbortController para un contexto específico
   * @param context - Identificador del contexto (ej: 'dashboard', 'orders', etc.)
   * @returns AbortSignal para usar en la petición
   */
  createSignal(context: string): AbortSignal {
    const controller = new AbortController();

    if (!this.controllers.has(context)) {
      this.controllers.set(context, []);
    }

    this.controllers.get(context)!.push(controller);

    return controller.signal;
  }

  /**
   * Cancela todas las peticiones pendientes de un contexto específico
   * @param context - Identificador del contexto a cancelar
   */
  cancelContext(context: string): void {
    const controllers = this.controllers.get(context);

    if (controllers) {
      controllers.forEach((controller) => {
        controller.abort();
      });
      this.controllers.delete(context);
    }
  }

  /**
   * Cancela todas las peticiones pendientes de todos los contextos
   */
  cancelAll(): void {
    this.controllers.forEach((controllers) => {
      controllers.forEach((controller) => {
        controller.abort();
      });
    });
    this.controllers.clear();
  }

  /**
   * Limpia los controllers completados de un contexto
   * @param context - Identificador del contexto a limpiar
   */
  cleanupContext(context: string): void {
    const controllers = this.controllers.get(context);

    if (controllers) {
      // Filtrar solo los controllers que no han sido abortados
      const activeControllers = controllers.filter(
        (controller) => !controller.signal.aborted
      );

      if (activeControllers.length > 0) {
        this.controllers.set(context, activeControllers);
      } else {
        this.controllers.delete(context);
      }
    }
  }
}

export const requestManager = new RequestCancellationManager();

/**
 * Helper para verificar si un error es de cancelación
 */
export const isRequestCancelled = (error: unknown): boolean => axios.isCancel(error);

// ----------------------------------------------------------------------

export const fetcherGet = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    // No logear errores de cancelación
    if (!axios.isCancel(error)) {
      console.error('Failed to fetch:', error);
    }
    throw error;
  }
};

/**
 * Crea un fetcher para SWR con soporte de cancelación
 * @param context - Contexto de cancelación (ej: 'dashboard', 'orders')
 * @returns Fetcher function para usar con SWR
 */
export const createCancellableFetcher = (context: string) => async (args: string | [string, AxiosRequestConfig]) => {
    const [url, config] = Array.isArray(args) ? args : [args];
    const signal = requestManager.createSignal(context);

    try {
      const res = await axiosInstance.get(url, { ...config, signal });
      return res.data;
    } catch (error) {
      // No logear errores de cancelación
      if (!axios.isCancel(error)) {
        console.error('Failed to fetch:', error);
      }
      throw error;
    }
  };

export const fetcherPost = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.post(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

export const fetcherPut = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.put(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

export const fetcherDelete = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.delete(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

export const fetcher = async (url: string, config?: AxiosRequestConfig) => {
  try {
    const res = await axiosInstance({ url, ...config });
    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/admin/auth/me',
    signIn: '/api/admin/auth/sign-in',
    //signUp: '/api/auth/sign-up',
    //signIn: '/api/auth/sign-in'
  },
  outreach: {
    getPaginatedOutreaches: '/api/admin/outreach/paginated',
    getOutreachById: (id: number) => `/api/admin/outreach/${id}`,
    updateOutreach: (id: number) => `/api/admin/outreach/${id}`,
    updateStatus: (id: number) => `/api/admin/outreach/${id}/status`,
    createOutreach: '/api/admin/outreach/create',
    importUrls: '/api/admin/outreach/import-urls',
    exportExcel: '/api/admin/outreach/export-excel',
    deleteOutreach: (id: number) => `/api/admin/outreach/${id}`,
    pendingCount: '/api/admin/outreach/pending-count',
    markAsUploaded: (id: number) => `/api/admin/outreach/${id}/mark-as-uploaded`,
    fetchTraffic: (id: number) => `/api/admin/outreach/${id}/fetch-traffic`,
    extractEmail: (id: number) => `/api/admin/outreach/${id}/extract-email`,
    sendContactEmail: (id: number) => `/api/admin/outreach/${id}/send-contact-email`,
    bulkDelete: '/api/admin/outreach/bulk-delete',
  },
  invoice: {
    generatePlatformInvoice: '/api/admin/invoice/platform',
    downloadPlatformInvoice: (id: number) => `/api/admin/invoice/${id}/pdf`,
    getPaginatedInvoices: '/api/admin/invoice',
    getInvoiceById: (id: number) => `/api/admin/invoice/${id}`,
    updateInvoice: (id: number) => `/api/admin/invoice/${id}`,
    downloadPdf: (id: number) => `/api/admin/invoice/${id}/pdf`,
    cancelInvoice: '/api/admin/invoice/cancel',
    acceptPayment: (id: number) => `/api/admin/invoice/${id}/accept-payment`,
    exportExcel: '/api/admin/invoice/export-excel',
    bulkUpdateStatus: '/api/admin/invoice/bulk-update-status',
    createManual: '/api/admin/invoice/manual',
    removeOrder: (invoiceId: number, orderId: number) => `/api/admin/invoice/${invoiceId}/order/${orderId}`,
  },
  dashboard: {
    pendingOrders: '/api/admin/dashboard/pending-orders',
    pendingMedias: '/api/admin/dashboard/pending-medias',
    dailyProfit: '/api/admin/dashboard/daily-profit',
    monthlyProfit: '/api/admin/dashboard/monthly-profit',
    newUsers: '/api/admin/dashboard/new-users',
    billingChart: '/api/admin/dashboard/billing-chart',
    totalBalance: '/api/admin/dashboard/total-balance',
    summary: '/api/admin/dashboard/summary',
    notificationCheck: '/api/admin/dashboard/notification-check',
    sidebarCounts: '/api/admin/dashboard/sidebar-counts',
  },
  orders: {
    getOrders: '/api/admin/order',
    getOrder: (id: number) => `/api/admin/order/${id}`,
    updateOrder: (id: number) => `/api/admin/order/${id}`,
    deleteOrder: (id: number) => `/api/admin/order/${id}`,
    addNoteToOrder: (id: number) => `/api/admin/order/${id}/notes`,
    sendReminder: '/api/admin/order/send-reminder',
    sendPaymentReminder: '/api/admin/order/send-payment-reminder',
    sendPendingAcceptanceReminder: '/api/admin/order/send-pending-acceptance-reminder',
    sendJournalistContentReminder: '/api/admin/order/send-journalist-content-reminder',
    calendar: (year: number, month: number) => `/api/admin/order/calendar/${year}/${month}`,
    searchByUrl: '/api/admin/order/search-by-url',
    externals: {
      pendingPaymentsCount: '/api/admin/order/pending-external-count',
      createExternalOrder: '/api/admin/order/external',
    },
    downloadReceipt: (id: number) => `/api/admin/order/${id}/receipt`,
  },
  medias: {
    getAll: '/api/admin/media',
    getOne: (id: number) => `/api/admin/media/${id}`,
    update: (id: number) => `/api/admin/media/${id}`,
    create: '/api/admin/media/create',
    updateMetrics: (id: number) => `/api/admin/media/${id}/update-metrics`,
    import: '/api/admin/media/import',
    analyzeCategoryCommissions: '/api/admin/media/analyze-category-commissions',
    approveEditor: (id: number) => `/api/admin/media/${id}/approve-editor`,
    rejectEditor: (id: number) => `/api/admin/media/${id}/reject-editor`,
    bulkApprove: '/api/admin/media/bulk-approve',
    bulkReject: '/api/admin/media/bulk-reject',
    bulkApproveByFilter: '/api/admin/media/bulk-approve-by-filter',
    bulkRejectByFilter: '/api/admin/media/bulk-reject-by-filter',
    pendingCount: '/api/admin/media/pending-count',
    queueScreenshot: (id: number) => `/api/admin/media/${id}/queue-screenshot`,
    queueDomainAge: (id: number) => `/api/admin/media/${id}/queue-domain-age`,
    addKeyword: (id: number) => `/api/admin/media/${id}/keywords`,
    deleteKeyword: (id: number, keywordId: number) => `/api/admin/media/${id}/keywords/${keywordId}`,
  },
  categories: {
    getCategories: '/api/admin/category',
    getCategory: (id: number) => `/api/admin/category/${id}`,
    updateCategory: (id: number) => `/api/admin/category/${id}`,
  },
  customers: {
    getRedactors: 'api/admin/customer/redactors',
    getAll: '/api/admin/customer',
    getOne: (id: number) => `/api/admin/customer/${id}`,
    create: '/api/admin/customer/create',
    update: (id: number) => `/api/admin/customer/${id}`,
    delete: (id: number) => `/api/admin/customer/${id}`,
    generatePassword: (id: number) => `/api/admin/customer/${id}/password`,
    sendPasswordEmail: (id: number) => `/api/admin/customer/${id}/send-password-email`,
    createPayout: '/api/admin/customer/payout',
    walletUpdate: '/api/admin/customer/wallet-update',
    impersonate: (id: number) => `/api/admin/customer/${id}/impersonate`,
    importMedia: '/api/admin/customer/import-media',
    checkMediaUrls: '/api/admin/customer/check-media-urls',
    checkMediaItems: '/api/admin/customer/check-media-items',
    mediaImportTemplate: '/api/admin/customer/media-import-template',
  },
  platforms: {
    getAll: '/api/admin/platform',
    getOne: (id: string) => `/api/admin/platform/${id}`,
    updatePlatform: (id: string) => `/api/admin/platform/${id}`,
    createPlatform: '/api/admin/platform/create',
    markOrdersAsPaid: '/api/admin/platform/mark-orders-as-paid',
  },
  coupons: {
    getAll: '/api/admin/coupon',
    getById: (id: number) => `/api/admin/coupon/${id}`,
    create: '/api/admin/coupon',
    update: (id: number) => `/api/admin/coupon/${id}`,
    delete: (id: number) => `/api/admin/coupon/${id}`,
    usageCounts: '/api/admin/coupon/usage-counts',
    usageDetails: (id: number) => `/api/admin/coupon/${id}/usage-details`,
  },
  mediaProposals: {
    getAll: '/api/admin/mediaproposal',
    getById: (id: number) => `/api/admin/mediaproposal/${id}`,
    create: '/api/admin/mediaproposal',
    update: (id: number) => `/api/admin/mediaproposal/${id}`,
    delete: (id: number) => `/api/admin/mediaproposal/${id}`,
    pendingCount: '/api/admin/mediaproposal/pending-count',
  },
  expenseInvoices: {
    getAll: '/api/admin/expense-invoice',
    getById: (id: number) => `/api/admin/expense-invoice/${id}`,
    create: '/api/admin/expense-invoice/create',
    update: (id: number) => `/api/admin/expense-invoice/${id}`,
    delete: (id: number) => `/api/admin/expense-invoice/${id}`,
    viewFile: (id: number) => `/api/admin/expense-invoice/${id}/file`,
    exportExcel: '/api/admin/expense-invoice/export-excel',
    downloadZip: '/api/admin/expense-invoice/download-zip',
    downloadAttachments: '/api/admin/expense-invoice/download-attachments',
  },
  adminUsers: {
    getAll: '/api/admin/admin-users',
    getPaginated: '/api/admin/admin-users/paginated',
    getById: (id: number) => `/api/admin/admin-users/${id}`,
    create: '/api/admin/admin-users',
    update: (id: number) => `/api/admin/admin-users/${id}`,
    delete: (id: number) => `/api/admin/admin-users/${id}`,
    generatePassword: (id: number) => `/api/admin/admin-users/${id}/generate-password`,
    pendingJournalistsCount: '/api/admin/admin-users/pending-journalists-count',
    pendingJournalists: '/api/admin/admin-users/pending-journalists',
    validateJournalist: (id: number) => `/api/admin/admin-users/${id}/validate-journalist`,
    rejectJournalist: (id: number) => `/api/admin/admin-users/${id}/reject-journalist`,
    toggleStatus: (id: number) => `/api/admin/admin-users/${id}/toggle-status`,
  },
  banners: {
    getAll: '/api/admin/banneradmin',
    getById: (id: number) => `/api/admin/banneradmin/${id}`,
    create: '/api/admin/banneradmin',
    update: (id: number) => `/api/admin/banneradmin/${id}`,
    delete: (id: number) => `/api/admin/banneradmin/${id}`,
    getAllActive: '/api/admin/banneradmin/all',
  },
  gmbReviewPacks: {
    list: '/api/admin/GmbReviewPack',
    details: (id: number) => `/api/admin/GmbReviewPack/${id}`,
    byPackId: (packId: string) => `/api/admin/GmbReviewPack/packId/${packId}`,
    create: '/api/admin/GmbReviewPack',
    update: (id: number) => `/api/admin/GmbReviewPack/${id}`,
    delete: (id: number) => `/api/admin/GmbReviewPack/${id}`,
  },
  pressReleasePacks: {
    list: '/api/admin/PressReleasePack',
    details: (id: number) => `/api/admin/PressReleasePack/${id}`,
    byPackId: (packId: string) => `/api/admin/PressReleasePack/packId/${packId}`,
    create: '/api/admin/PressReleasePack',
    update: (id: number) => `/api/admin/PressReleasePack/${id}`,
    delete: (id: number) => `/api/admin/PressReleasePack/${id}`,
  },
  gmbBoosterPacks: {
    list: '/api/admin/GmbBoosterPack',
    details: (id: number) => `/api/admin/GmbBoosterPack/${id}`,
    byPackId: (packId: string) => `/api/admin/GmbBoosterPack/packId/${packId}`,
    create: '/api/admin/GmbBoosterPack',
    update: (id: number) => `/api/admin/GmbBoosterPack/${id}`,
    delete: (id: number) => `/api/admin/GmbBoosterPack/${id}`,
  },
  localCitationPacks: {
    list: '/api/admin/LocalCitationPack',
    details: (id: number) => `/api/admin/LocalCitationPack/${id}`,
    byPackId: (packId: string) => `/api/admin/LocalCitationPack/packId/${packId}`,
    create: '/api/admin/LocalCitationPack',
    update: (id: number) => `/api/admin/LocalCitationPack/${id}`,
    delete: (id: number) => `/api/admin/LocalCitationPack/${id}`,
  },
  mailgun: {
    templates: '/api/mailgun/templates',
  },
  managedServiceSubscriptions: {
    list: '/api/admin/ManagedServiceSubscriptionAdmin',
    details: (id: number) => `/api/admin/ManagedServiceSubscriptionAdmin/${id}`,
    byUser: (userId: number) => `/api/admin/ManagedServiceSubscriptionAdmin/user/${userId}`,
    active: '/api/admin/ManagedServiceSubscriptionAdmin/active',
    update: (id: number) => `/api/admin/ManagedServiceSubscriptionAdmin/${id}`,
    delete: (id: number) => `/api/admin/ManagedServiceSubscriptionAdmin/${id}`,
    toggleStatus: (id: number) => `/api/admin/ManagedServiceSubscriptionAdmin/${id}/toggle-status`,
  },
  paymentRequests: {
    getAll: '/api/admin/transfer-requests',
    getById: (id: number) => `/api/admin/transfer-requests/${id}`,
    pendingEditorCount: '/api/admin/transfer-requests/pending-editor-count',
    pendingPaymentEditorCount: '/api/admin/transfer-requests/pending-payment-editor-count',
    approve: (id: number) => `/api/admin/transfer-requests/${id}/approve`,
    reject: (id: number) => `/api/admin/transfer-requests/${id}/reject`,
    markAsPaid: (id: number) => `/api/admin/transfer-requests/${id}/mark-as-paid`,
    downloadInvoice: (id: number) => `/api/admin/transfer-requests/${id}/invoice-file`,
    updateInvoiceFile: (id: number) => `/api/admin/transfer-requests/${id}/invoice-file`,
    updatePaymentMethod: (id: number) => `/api/admin/transfer-requests/${id}/payment-method`,
    createManual: '/api/admin/transfer-requests/create-manual',
  },
  mediaPack: {
    list: '/api/admin/media-packs',
    details: (id: number) => `/api/admin/media-packs/${id}`,
    create: '/api/admin/media-packs',
    update: (id: number) => `/api/admin/media-packs/${id}`,
    delete: (id: number) => `/api/admin/media-packs/${id}`,
    restore: (id: number) => `/api/admin/media-packs/${id}/restore`,
    toggleStatus: (id: number) => `/api/admin/media-packs/${id}/toggle-status`,
    deleted: '/api/admin/media-packs/deleted',
    eligibleMedia: '/api/admin/media-packs/eligible-media',
    suggestMedia: '/api/admin/media-packs/suggest-media',
  },
  editorNewsletters: {
    paginated: '/api/admin/editor-newsletters/paginated',
    details: (id: number) => `/api/admin/editor-newsletters/${id}`,
    create: '/api/admin/editor-newsletters',
    update: (id: number) => `/api/admin/editor-newsletters/${id}`,
    delete: (id: number) => `/api/admin/editor-newsletters/${id}`,
    categories: '/api/admin/editor-newsletters/categories',
  },
  newsletterAdsRequests: {
    paginated: '/api/admin/newsletter-ads-requests/paginated',
    details: (id: number) => `/api/admin/newsletter-ads-requests/${id}`,
    update: (id: number) => `/api/admin/newsletter-ads-requests/${id}`,
    review: (id: number) => `/api/admin/newsletter-ads-requests/${id}/review`,
    delete: (id: number) => `/api/admin/newsletter-ads-requests/${id}`,
    pendingCount: '/api/admin/newsletter-ads-requests/pending-count',
  },
  bannedWords: {
    list: '/api/admin/bannedword',
    details: (id: number) => `/api/admin/bannedword/${id}`,
    create: '/api/admin/bannedword',
    createBulk: '/api/admin/bannedword/bulk',
    update: (id: number) => `/api/admin/bannedword/${id}`,
    delete: (id: number) => `/api/admin/bannedword/${id}`,
    toggle: (id: number) => `/api/admin/bannedword/${id}/toggle`,
  },
  orderMessages: {
    pending: '/api/admin/ordermessage/pending',
    all: '/api/admin/ordermessage/all',
    byOrder: (orderId: number) => `/api/admin/ordermessage/order/${orderId}`,
    approve: (id: number) => `/api/admin/ordermessage/${id}/approve`,
    reject: (id: number) => `/api/admin/ordermessage/${id}/reject`,
    edit: (id: number) => `/api/admin/ordermessage/${id}/edit`,
    stats: '/api/admin/ordermessage/stats',
  },
};
